import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Image, Minimize2, TableProperties, Bot, Zap } from 'lucide-react';
import SEOOverviewAuditDetail from './SEOOverviewAuditDetail';
import {
  altTextOpportunity,
  computeRecommendedAction,
  computeTopOpportunities,
  countHighImpactOpportunities,
} from '../utils/dashboardRecommendations';
import * as logger from '../utils/logger';
import './dashboard-action-first.css';

const apiUrl = import.meta.env.VITE_BACKEND_URL;
const llmsTxtEnabled = (() => {
  const v = import.meta.env.VITE_LLMS_TXT_ENABLED;
  if (v === undefined || v === '') return true;
  const n = String(v).toLowerCase();
  return n === 'true' || n === '1';
})();
const indexNowFeatureEnabled = (() => {
  const v = import.meta.env.VITE_INDEXNOW_ENABLED;
  if (v === undefined || v === '') return true;
  const n = String(v).toLowerCase();
  return n === 'true' || n === '1';
})();

function scoreLabelKey(score) {
  if (score > 95) return 'excellent';
  if (score >= 80) return 'goodFoundation';
  if (score >= 60) return 'roomToImprove';
  return 'needsAttention';
}

function impactBadgeClass(impact) {
  if (impact === 'High') return 'ss-dash-impact ss-dash-impact--high';
  if (impact === 'Medium') return 'ss-dash-impact ss-dash-impact--medium';
  return 'ss-dash-impact ss-dash-impact--low';
}

export default function DashboardActionFirst({
  storeId,
  onNavigate,
  validationDisabled,
  quotaExceeded,
  refreshTrigger,
  onQuotaExceeded,
  onFixErrors,
  onQuickAction,
}) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [llmsStatus, setLlmsStatus] = useState('unknown');
  const [indexNowEnabled, setIndexNowEnabled] = useState(false);
  const [featureSetupLoading, setFeatureSetupLoading] = useState(true);

  useEffect(() => {
    if (!storeId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${apiUrl}/api/seo-cache/overview-stats?id=${storeId}&_t=${Date.now()}`
        );
        if (!res.ok) throw new Error('Failed to load dashboard stats');
        const data = await res.json();
        if (!cancelled) {
          setStats(data);
          const quota = data.cacheMetadata?.quota_usage;
          if (
            quota &&
            !quota.unlimited &&
            (quota.remaining || 0) <= 0 &&
            onQuotaExceeded
          ) {
            onQuotaExceeded({
              used: quota.used,
              limit: quota.limit,
              accountType: quota.accountType || 'trial',
            });
          }
        }
      } catch (e) {
        logger.error('[DashboardActionFirst] load failed', e);
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [storeId, refreshTrigger, onQuotaExceeded]);

  const recommended = useMemo(() => (stats ? computeRecommendedAction(stats) : null), [stats]);
  const opportunities = useMemo(() => (stats ? computeTopOpportunities(stats) : []), [stats]);
  const highImpactCount = useMemo(() => (stats ? countHighImpactOpportunities(stats) : 0), [stats]);
  const altOpp = stats ? altTextOpportunity(stats) : 0;

  useEffect(() => {
    if (!storeId) {
      setFeatureSetupLoading(false);
      return;
    }
    let cancelled = false;

    const loadFeatureSetup = async () => {
      setFeatureSetupLoading(true);
      try {
        const jobs = [];
        if (llmsTxtEnabled) {
          jobs.push(
            fetch(`${apiUrl}/api/llms-txt/settings?id=${encodeURIComponent(storeId)}`)
              .then(async (res) => (res.ok ? res.json() : null))
              .then((data) => ({ kind: 'llms', data }))
              .catch(() => ({ kind: 'llms', data: null })),
          );
        }
        if (indexNowFeatureEnabled) {
          jobs.push(
            fetch(`${apiUrl}/api/indexnow/settings?hashSiteId=${encodeURIComponent(storeId)}`)
              .then(async (res) => (res.ok ? res.json() : null))
              .then((data) => ({ kind: 'indexnow', data }))
              .catch(() => ({ kind: 'indexnow', data: null })),
          );
        }

        const results = await Promise.all(jobs);
        if (cancelled) return;

        let nextLlmsStatus = 'disabled';
        let nextIndexNowEnabled = false;

        if (llmsTxtEnabled) {
          nextLlmsStatus = 'not_created';
          const llms = results.find((r) => r.kind === 'llms')?.data;
          nextLlmsStatus = llms?.settings?.status || 'not_created';
        }
        if (indexNowFeatureEnabled) {
          const indexNow = results.find((r) => r.kind === 'indexnow')?.data;
          nextIndexNowEnabled = Boolean(indexNow?.settings?.enabled);
        }

        setLlmsStatus(nextLlmsStatus);
        setIndexNowEnabled(nextIndexNowEnabled);
      } catch (e) {
        logger.error('[DashboardActionFirst] feature setup load failed', e);
        if (!cancelled) {
          setLlmsStatus(llmsTxtEnabled ? 'not_created' : 'disabled');
          setIndexNowEnabled(false);
        }
      } finally {
        if (!cancelled) setFeatureSetupLoading(false);
      }
    };

    void loadFeatureSetup();
    return () => {
      cancelled = true;
    };
  }, [storeId, refreshTrigger]);

  const nextSteps = useMemo(() => {
    const steps = [];
    if (llmsTxtEnabled && llmsStatus !== 'created') {
      steps.push({
        id: 'llms',
        titleKey: 'dashboardActionFirst.nextLlmsTitle',
        descriptionKey: 'dashboardActionFirst.nextLlmsDesc',
        ctaKey: 'dashboardActionFirst.nextLlmsCta',
        navigate: { tab: 'llms-txt' },
      });
    }
    if (indexNowFeatureEnabled && !indexNowEnabled) {
      steps.push({
        id: 'indexnow',
        titleKey: 'dashboardActionFirst.nextIndexNowTitle',
        descriptionKey: 'dashboardActionFirst.nextIndexNowDesc',
        ctaKey: 'dashboardActionFirst.nextIndexNowCta',
        navigate: { tab: 'indexnow' },
      });
    }
    return steps;
  }, [indexNowEnabled, llmsStatus, llmsTxtEnabled, indexNowFeatureEnabled]);

  const handleRecommendedCta = () => {
    if (!recommended) return;
    onNavigate(recommended.navigate);
  };

  const impactLabel = (impact) => {
    if (impact === 'High') return t('dashboardActionFirst.impactHigh');
    if (impact === 'Medium') return t('dashboardActionFirst.impactMedium');
    return t('dashboardActionFirst.impactLow');
  };

  return (
    <div className="ss-dashboard-guided">
      <div className="ss-dash-page-header">
        <h5 className="fw-semibold mb-0 ss-dash-page-title">
          {t('dashboardActionFirst.pageTitle')}
        </h5>
      </div>

      <section className="ss-dash-hero card border-0 shadow-sm ss-dash-section" id="tour-dashboard-overview">
        <div className="card-body ss-dash-hero__body">
          <div className="row g-3 align-items-stretch">
            <div className="col-12 col-md-4 col-xl-3">
              <div className="ss-dash-hero__score-panel h-100">
                <div className="ss-dash-hero__score-label">
                  {t('dashboardActionFirst.seoHealth')}
                </div>
                <div className="ss-dash-hero__score">
                  {loading || !stats ? '…' : stats.overallScore}
                  <span>/100</span>
                </div>
                <div className="ss-dash-hero__score-caption">
                  {loading || !stats
                    ? t('dashboardActionFirst.loading')
                    : t(`dashboardActionFirst.${scoreLabelKey(stats.overallScore)}`)}
                </div>
                {!loading && stats && highImpactCount > 0 ? (
                  <div className="ss-dash-hero__score-note">
                    {t('dashboardActionFirst.highImpactCount', { count: highImpactCount, defaultValue: `${highImpactCount} high-impact opportunit${highImpactCount === 1 ? 'y' : 'ies'}` })}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="col-12 col-md-8 col-xl-9">
              <div className="ss-dash-hero__action-box h-100">
                <div className="ss-dash-hero__action-label">
                  {t('dashboardActionFirst.recommendedAction')}
                </div>
                {loading || !recommended ? (
                  <p className="text-muted mb-0">{t('dashboardActionFirst.loadingRecommendations')}</p>
                ) : (
                  <>
                    <h3 className="ss-dash-hero__action-title">
                      {t(recommended.titleKey, recommended.titleParams || {})}
                    </h3>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <span className={impactBadgeClass(recommended.impact)}>
                        {t('dashboardActionFirst.impactLabel', { impact: impactLabel(recommended.impact) })}
                      </span>
                      {recommended.scoreGain > 0 ? (
                        <span className="ss-dash-meta-pill">
                          {t('dashboardActionFirst.estScoreGain', { gain: recommended.scoreGain })}
                        </span>
                      ) : null}
                      <span className="ss-dash-meta-pill">
                        {t('dashboardActionFirst.timeEstimate', { time: t(recommended.timeKey) })}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-auto">
                      <button
                        type="button"
                        className="btn btn-primary fw-semibold"
                        onClick={handleRecommendedCta}
                      >
                        {t(recommended.ctaKey)}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary fw-semibold"
                        onClick={() => onNavigate({ tab: 'seo-optimizer', subTab: 'overview' })}
                      >
                        {t('dashboardActionFirst.viewFullPlan')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {(llmsTxtEnabled || indexNowFeatureEnabled) && (
        <section className="ss-dash-section">
          <div className="ss-dash-section-head">
            <h6 className="fw-bold mb-0">{t('dashboardActionFirst.growthTitle')}</h6>
          </div>
          <div className="row g-3">
            {llmsTxtEnabled && (
              <GrowthCard
                title="LLMs.txt"
                icon={<Bot size={18} />}
                accent="#7c3aed"
                description={t('dashboardActionFirst.growthLlmsDesc')}
                status={llmsStatus === 'created'
                  ? t('dashboardActionFirst.growthLlmsStatusLive')
                  : t('dashboardActionFirst.growthStatusPending')}
                impact={t('dashboardActionFirst.growthLlmsImpact')}
                cta={llmsStatus === 'created'
                  ? t('dashboardActionFirst.growthLlmsCtaManage')
                  : t('dashboardActionFirst.growthLlmsCtaSetup')}
                onClick={() => onNavigate({ tab: 'llms-txt' })}
              />
            )}
            {indexNowFeatureEnabled && (
              <GrowthCard
                title="IndexNow"
                icon={<Zap size={18} />}
                accent="#ea580c"
                description={t('dashboardActionFirst.growthIndexNowDesc')}
                status={indexNowEnabled
                  ? t('dashboardActionFirst.growthIndexNowStatusLive')
                  : t('dashboardActionFirst.growthStatusPending')}
                impact={t('dashboardActionFirst.growthIndexNowImpact')}
                cta={indexNowEnabled
                  ? t('dashboardActionFirst.growthIndexNowCtaOpen')
                  : t('dashboardActionFirst.growthIndexNowCtaEnable')}
                onClick={() => onNavigate({ tab: 'indexnow' })}
              />
            )}
          </div>
        </section>
      )}

      <div className="row g-3 ss-dash-section">
        <div className="col-12">
          {opportunities.length > 0 ? (
            <section className="card border-0 shadow-sm h-100">
              <div className="card-body p-2">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                  <h6 className="fw-bold mb-0">{t('dashboardActionFirst.topOpportunities')}</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary fw-semibold"
                    onClick={() => onNavigate({ tab: 'seo-optimizer', subTab: 'overview' })}
                  >
                    {t('dashboardActionFirst.viewAll')}
                  </button>
                </div>
                <div className="ss-dash-opportunity-list">
                  {opportunities.map((opp, idx) => (
                    <button
                      key={opp.id}
                      type="button"
                      className="ss-dash-opportunity"
                      onClick={() => onNavigate(opp.navigate)}
                    >
                      <span className="ss-dash-opportunity__rank">{idx + 1}</span>
                      <span className="ss-dash-opportunity__body">
                        <span className="ss-dash-opportunity__title">{t(opp.labelKey)}</span>
                        <span className="ss-dash-opportunity__meta">
                          {t('dashboardActionFirst.itemCount', {
                            count: opp.count,
                            defaultValue: `${opp.count.toLocaleString()} ${opp.count === 1 ? 'item' : 'items'}`,
                          })}
                        </span>
                      </span>
                      <span className={impactBadgeClass(opp.impact)}>
                        {t('dashboardActionFirst.impactSuffix', { impact: impactLabel(opp.impact) })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          ) : (
            <section className="card border-0 shadow-sm h-100">
              <div className="card-body p-3 d-flex flex-column justify-content-center">
                <h6 className="fw-bold mb-1">{t('dashboardActionFirst.topOpportunities')}</h6>
                <p className="text-muted small mb-2">{t('dashboardActionFirst.noOpportunities')}</p>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary fw-semibold align-self-start"
                  onClick={() => onNavigate({ tab: 'seo-optimizer', subTab: 'overview' })}
                >
                  {t('dashboardActionFirst.openAiOptimization')}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>

      <section className="ss-dash-section">
        <div className="ss-dash-section-head">
          <h6 className="fw-bold mb-0">{t('dashboardActionFirst.toolkitTitle')}</h6>
        </div>
        <div className="row g-3">
          <ToolkitCard
            title={t('dashboardActionFirst.aiOptimizationTitle')}
            icon={<LineChart size={18} />}
            accent="#7c3aed"
            bullets={[
              t('dashboardActionFirst.aiBullet1'),
              t('dashboardActionFirst.aiBullet2'),
              t('dashboardActionFirst.aiBullet3'),
            ]}
            usage={
              stats
                ? t('dashboardActionFirst.aiUsage', {
                    current: stats.pagesAudited.current.toLocaleString(),
                    total: stats.pagesAudited.total.toLocaleString(),
                  })
                : t('dashboardActionFirst.loading')
            }
            cta={t('dashboardActionFirst.tryAiSeo')}
            onClick={() => onNavigate({ tab: 'seo-optimizer', subTab: 'overview' })}
          />
          <ToolkitCard
            title={t('dashboardActionFirst.altTextTitle')}
            icon={<Image size={18} />}
            accent="#059669"
            bullets={[
              t('dashboardActionFirst.altBullet1'),
              t('dashboardActionFirst.altBullet2'),
              t('dashboardActionFirst.altBullet3'),
            ]}
            usage={
              altOpp > 0
                ? t('dashboardActionFirst.altUsageMissing', { count: altOpp.toLocaleString() })
                : t('dashboardActionFirst.altUsageCaughtUp')
            }
            impact={
              altOpp > 0
                ? t('dashboardActionFirst.altImpact', {
                    points: Math.min(15, Math.max(3, Math.ceil(altOpp * 0.12))),
                  })
                : undefined
            }
            cta={t('dashboardActionFirst.generateAltText')}
            onClick={() => onNavigate({ tab: 'alt-text-generator' })}
          />
          <ToolkitCard
            title={t('dashboardActionFirst.imageOptTitle')}
            icon={<Minimize2 size={18} />}
            accent="#2563eb"
            bullets={[
              t('dashboardActionFirst.imageBullet1'),
              t('dashboardActionFirst.imageBullet2'),
              t('dashboardActionFirst.imageBullet3'),
            ]}
            usage={
              stats
                ? t('dashboardActionFirst.imageUsage', {
                    optimized: (stats.imageOptimization?.optimized ?? 0).toLocaleString(),
                    total: (stats.imageOptimization?.total ?? 0).toLocaleString(),
                  })
                : t('dashboardActionFirst.loading')
            }
            impact={t('dashboardActionFirst.imageImpact')}
            cta={t('dashboardActionFirst.optimizeImages')}
            onClick={() => onNavigate({ tab: 'image-optimizer' })}
          />
          <ToolkitCard
            title={t('dashboardActionFirst.bulkEditorTitle')}
            icon={<TableProperties size={18} />}
            accent="#0ea5e9"
            bullets={[
              t('dashboardActionFirst.bulkBullet1'),
              t('dashboardActionFirst.bulkBullet2'),
              t('dashboardActionFirst.bulkBullet3'),
            ]}
            usage={t('dashboardActionFirst.bulkUsage')}
            cta={t('dashboardActionFirst.openEditor')}
            onClick={() => onNavigate({ tab: 'seo-bulk' })}
          />
        </div>
      </section>

      <section className="ss-dash-section">
        <div className="ss-dash-section-head">
          <h6 className="fw-bold mb-0">
            {featureSetupLoading || nextSteps.length > 0
              ? t('dashboardActionFirst.recommendedNextSteps')
              : t('dashboardActionFirst.detailedAudit')}
          </h6>
        </div>
        {featureSetupLoading ? (
          <section className="card border-0 shadow-sm">
            <div className="card-body text-muted">{t('dashboardActionFirst.loadingNextSteps')}</div>
          </section>
        ) : nextSteps.length > 0 ? (
          <div className="row g-3">
            {nextSteps.map((step) => (
              <div key={step.id} className="col-12 col-lg-6">
                <section className="card border-0 shadow-sm h-100">
                  <div className="card-body p-3 d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                      <h6 className="fw-bold mb-0">{t(step.titleKey)}</h6>
                      <span className="ss-dash-meta-pill">{t('dashboardActionFirst.pending')}</span>
                    </div>
                    <p className="text-muted small mb-3">{t(step.descriptionKey)}</p>
                    <div className="mt-auto">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary fw-semibold"
                        onClick={() => onNavigate(step.navigate)}
                      >
                        {t(step.ctaKey)}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            ))}
          </div>
        ) : (
          <SEOOverviewAuditDetail
            storeId={storeId}
            validationDisabled={validationDisabled}
            quotaExceeded={quotaExceeded}
            refreshTrigger={refreshTrigger}
            onFixErrors={onFixErrors}
            onQuickAction={onQuickAction}
          />
        )}
      </section>
    </div>
  );
}

function ToolkitCard({ title, icon, accent, bullets, usage, impact, cta, onClick }) {
  return (
    <div className="col-12 col-sm-6 col-lg-3">
      <div className="card border-0 shadow-sm h-100 ss-dash-toolkit-card">
        <div className="card-body p-3 d-flex flex-column">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span
              className="ss-dash-toolkit-icon"
              style={{ color: accent, background: `${accent}14` }}
            >
              {icon}
            </span>
            <h6 className="fw-bold mb-0" style={{ fontSize: '0.95rem' }}>
              {title}
            </h6>
          </div>
          <ul className="ss-dash-toolkit-bullets mb-2">
            {bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <p className="text-muted small mb-1 mt-auto">{usage}</p>
          {impact ? (
            <p className="small mb-2 fw-semibold" style={{ color: accent }}>
              {impact}
            </p>
          ) : null}
          <button type="button" className="btn btn-sm btn-outline-primary fw-semibold mt-1" onClick={onClick}>
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}

function GrowthCard({ title, icon, accent, description, status, impact, cta, onClick }) {
  return (
    <div className="col-12 col-xl-6">
      <div
        className="card border-0 shadow-sm h-100"
        style={{ background: `linear-gradient(135deg, ${accent}14 0%, #ffffff 42%)` }}
      >
        <div className="card-body p-4 d-flex flex-column">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-center gap-3">
              <span
                className="ss-dash-toolkit-icon"
                style={{ color: accent, background: `${accent}20`, width: 48, height: 48, fontSize: '1.1rem' }}
              >
                {icon}
              </span>
              <div>
                <div className="text-uppercase fw-semibold small" style={{ color: accent, letterSpacing: '0.04em' }}>
                  Growth feature
                </div>
                <h5 className="fw-bold mb-0">{title}</h5>
              </div>
            </div>
            <span className="ss-dash-meta-pill">{status}</span>
          </div>
          <p className="text-muted mb-3" style={{ maxWidth: 680 }}>
            {description}
          </p>
          <p className="fw-semibold mb-3" style={{ color: accent }}>
            {impact}
          </p>
          <div className="mt-auto">
            <button type="button" className="btn btn-primary fw-semibold" onClick={onClick}>
              {cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
